import { Chatbox } from "@talkjs/react";
import { User } from "@core/features/auth";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Sheet } from "@themes/pollinator/ui/Sheet";
import { Type } from "@themes/pollinator/ui/Type";
import { TalkJSData, type ResolvedChatCircle } from "@core/features/chat";
import { ChatCirclePicker } from "../ChatCirclePicker";
import { ChatCircleSwitcher } from "../ChatCircleSwitcher";

export type Props = {
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
  talkjsData: TalkJSData | null | undefined;
  user: User | null | undefined;
  /**
   * Outcome from `useResolvedChatCircle` — drives whether the body
   * renders a Chatbox, the picker, or the empty state. The container
   * owns the resolver call; the sheet stays declarative.
   */
  resolved: ResolvedChatCircle;
  /** Sets the persisted selection for the active program. */
  onSelectCircle: (circleId: number) => void;
};

export function ChatSheet({
  isLoading = false,
  isOpen,
  onClose,
  talkjsData,
  user,
  resolved,
  onSelectCircle,
}: Props) {
  const sessionReady = Boolean(
    talkjsData?.app_id && talkjsData?.user_id && talkjsData?.token
  );

  // The switcher only appears when there's something to switch to —
  // single-circle users (`auto`) and the picker state don't render it.
  const showSwitcher =
    sessionReady &&
    resolved.kind === "selected" &&
    resolved.availableCircles.length > 1;

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-lg overflow-auto"
      innerClassName="justify-start"
    >
      <div className="flex-1 flex flex-col p-0">
        <div className="px-4 py-8 border-b flex flex-col gap-2">
          <Type variant="h3">Cohort Chat</Type>
          {showSwitcher && resolved.kind === "selected" && (
            <ChatCircleSwitcher
              availableCircles={resolved.availableCircles}
              currentCircleId={resolved.circleId}
              onSelect={onSelectCircle}
            />
          )}
        </div>
        {isLoading || !talkjsData || !user ? (
          <Loader isFullPage />
        ) : (
          <ChatSheetBody
            sessionReady={sessionReady}
            resolved={resolved}
            onSelectCircle={onSelectCircle}
          />
        )}
      </div>
    </Sheet>
  );
}

type BodyProps = {
  sessionReady: boolean;
  resolved: ResolvedChatCircle;
  onSelectCircle: (circleId: number) => void;
};

/**
 * Body of the sheet — split out so the four resolution branches read
 * top-to-bottom without nested ternaries.
 */
function ChatSheetBody({ sessionReady, resolved, onSelectCircle }: BodyProps) {
  // Without a session JWT the SDK can't connect at all; render the
  // empty state regardless of the resolver's verdict.
  if (!sessionReady) {
    return <ChatSheetEmptyState />;
  }

  if (resolved.kind === "empty") {
    return <ChatSheetEmptyState />;
  }

  if (resolved.kind === "needs-picker") {
    return (
      <ChatCirclePicker
        availableCircles={resolved.availableCircles}
        onSelect={onSelectCircle}
      />
    );
  }

  // 'auto' or 'selected' — both render the resolved conversation.
  // Keying on `conversationId` forces React to remount the Chatbox when
  // the user switches circles via the header switcher. Without the
  // remount, @talkjs/react updates the existing instance in place and
  // the previous conversation stays on screen until the new one syncs
  // — the `loadingComponent` only fires on initial mount, not on
  // prop changes. The remount surfaces the loader for the duration
  // of the TalkJS sync.
  return (
    <div className="flex flex-1 flex-col gap-4">
      <Chatbox
        key={resolved.conversationId}
        conversationId={resolved.conversationId}
        loadingComponent={<Loader />}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}

function ChatSheetEmptyState() {
  return (
    <div className="flex flex-col h-full gap-4 items-center justify-center">
      <Type variant="body2">No conversations found.</Type>
    </div>
  );
}

export default ChatSheet;
